import type {
  AlgorithmId,
  AlgorithmTune,
  HSL,
  LightPoint,
  RGB,
  RGBW,
  ZoneId,
} from '../types/ambilight';
import { analyzePerceptualZone } from './perceptualEngine';

export const ZONE_INFO_MAP: Record<ZoneId, { name: string; short: string; icon: string }> = {
  'top-left':     { name: 'Superior Izquierda', short: 'TL', icon: '↖' },
  'top':          { name: 'Arriba (Centro)',    short: 'T',  icon: '↑' },
  'top-right':    { name: 'Superior Derecha',   short: 'TR', icon: '↗' },
  'right':        { name: 'Derecha',            short: 'R',  icon: '→' },
  'bottom-right': { name: 'Inferior Derecha',   short: 'BR', icon: '↘' },
  'bottom':       { name: 'Abajo (Centro)',     short: 'B',  icon: '↓' },
  'bottom-left':  { name: 'Inferior Izquierda', short: 'BL', icon: '↙' },
  'left':         { name: 'Izquierda',          short: 'L',  icon: '←' },
  'center':       { name: 'Centro',             short: 'C',  icon: '●' },
  'all':          { name: 'Toda la Pantalla',   short: 'ALL',icon: '⬛' },
};

export const ALGORITHM_INFO_MAP: Record<
  AlgorithmId,
  { name: string; shortName: string; icon: string; desc: string }
> = {
  perceptual: {
    name: 'Motor Perceptual',
    shortName: 'Perceptual',
    icon: '🧠',
    desc: 'Cobertura sublineal + Saliencia + Movimiento + Contexto (anti-subtítulos y escenas complejas)',
  },
  weighted: {
    name: 'Ponderado',
    shortName: 'Ponderado',
    icon: '⚖️',
    desc: 'Mayor peso a píxeles brillantes: fiel, reactivo y ultra estable',
  },
  average: {
    name: 'Promedio',
    shortName: 'Promedio',
    icon: '📊',
    desc: 'Color medio de toda la zona muestreada',
  },
  dominant: {
    name: 'Color Dominante',
    shortName: 'Dominante',
    icon: '🎨',
    desc: 'Agrupa por similitud y escoge el grupo predominante',
  },
  median: {
    name: 'Mediana',
    shortName: 'Mediana',
    icon: '📐',
    desc: 'Filtra picos y valores extremos, movimiento suave',
  },
  saturated: {
    name: 'Más Saturado',
    shortName: 'Saturado',
    icon: '🌈',
    desc: 'Destaca el tono más vívido y potente de la zona',
  },
  dynamic: {
    name: 'Mix Dinámico',
    shortName: 'Dinámico',
    icon: '⚡',
    desc: 'Bloques 8×8 con realce de movimiento y memoria suave',
  },
};

export const DEFAULT_TUNE: AlgorithmTune = {
  weightedBeta: 1.5,
  weightedFloor: 0.01,
  dominantLevels: 16,
  dominantMinShare: 0,
  satMinBright: 30,
  satMinSat: 0,
  viz: false,
  perceptualProfile: 'cinematic',
  perceptualCoverageAlpha: 0.35,
  perceptualSaliencyWeight: 1.0,
  perceptualMotionWeight: 0.8,
  perceptualTextPenalty: 0.90,
  perceptualReactionSpeed: 0.50,
};

// Conversión RGB a HSL
export function rgbToHsl(r: number, g: number, b: number): HSL {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

// Conversión HSL a RGB
export function hslToRgb(h: number, s: number, l: number): RGB {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('').toUpperCase();
}

// Conversión lineal para mezcla sin pérdida de tono
export function srgbToLin(v: number): number {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function linToSrgb(v: number): number {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, c)) * 255);
}

export function mixLinear(c1: RGB, c2: RGB, t: number): RGB {
  if (t <= 0) return { ...c1 };
  if (t >= 1) return { ...c2 };
  const u = 1 - t;
  return {
    r: linToSrgb(srgbToLin(c1.r) * u + srgbToLin(c2.r) * t),
    g: linToSrgb(srgbToLin(c1.g) * u + srgbToLin(c2.g) * t),
    b: linToSrgb(srgbToLin(c1.b) * u + srgbToLin(c2.b) * t),
  };
}

// Síntesis de color 5 canales (R, G, B, C, W) de máxima fidelidad fotométrica para WiZ:
// Aprovecha los 5 chips LED físicos del foco:
// - C (Cold White 6500K D65): blanco puro de monitor / luz día
// - W (Warm White 2700K): luz cálida / fuego / atardecer / incandescente
// - R, G, B: croma puro sin saturar ni lavar
export function rgbToRgbw(r: number, g: number, b: number): RGBW {
  const fullR = Math.max(0, Math.min(255, Math.round(r)));
  const fullG = Math.max(0, Math.min(255, Math.round(g)));
  const fullB = Math.max(0, Math.min(255, Math.round(b)));

  // Componente neutro común a los 3 canales
  const m = Math.min(fullR, fullG, fullB);
  if (m <= 0) {
    return { r: fullR, g: fullG, b: fullB, c: 0, w: 0 };
  }

  // Factor de calidez cromática:
  // En luz blanca de monitor D65 (R ≈ B), warmth = 0 -> 100% canal c (blanco frío 6500K)
  // En escenas cálidas / luz de vela / fuego / atardecer (R > B), activa el canal w (blanco cálido 2700K)
  let warmth = 0;
  if (fullR > fullB) {
    warmth = Math.min(1.0, (fullR - fullB) / Math.max(fullR, 1));
    warmth = Math.pow(warmth, 1.2);
  }

  // Distribución del blanco entre blanco frío (c) y blanco cálido (w)
  const w = Math.round(m * warmth);
  const c = Math.round(m * (1 - warmth));

  // Descontar la emisión de los chips blancos de los canales RGB
  // El chip w aporta espectro continuo cálido (R ≈ 100%, G ≈ 70%, B ≈ 20%)
  // El chip c aporta espectro continuo frío D65 (R ≈ 100%, G ≈ 100%, B ≈ 100%)
  const remR = Math.max(0, fullR - c - w);
  const remG = Math.max(0, fullG - c - Math.round(w * 0.70));
  const remB = Math.max(0, fullB - c - Math.round(w * 0.20));

  return {
    r: Math.min(255, remR),
    g: Math.min(255, remG),
    b: Math.min(255, remB),
    c: Math.min(255, c),
    w: Math.min(255, w),
  };
}

// Ajuste perceptivo de color, saturación boost y curva gamma logarítmica
export function adjustColor(
  color: RGB,
  brightness: number,
  maxBrightness = 100,
  saturationBoost = 1.8,
  gamma = 0.4
): { color: RGB; dimming: number } {
  // -- Oscuridad absoluta: si la pantalla está negra, apagar el foco --
  // brightness < 2 significa píxeles prácticamente negros.
  // Devolvemos dimming=0 y negro puro para que wizClient envíe state:false.
  if (brightness < 2) {
    return { color: { r: 0, g: 0, b: 0 }, dimming: 0 };
  }

  const hsl = rgbToHsl(color.r, color.g, color.b);
  const s = Math.min(100, hsl.s * saturationBoost);

  // FIX CRÍTICO: NO aplicar piso mínimo de luminosidad HSL.
  // El piso de L >= 45 convertía el negro (L=0) en gris medio (L=45 → ~115,115,115)
  // haciendo que el foco nunca se apagara en escenas oscuras.
  // En cambio: para colores saturados recortamos el techo para no quemar el brillo;
  // para neutros/blancos se permite hasta 100%.
  const maxL = hsl.s < 5 ? 100 : Math.min(100, 75 + (1 - hsl.s / 100) * 25);
  // El mínimo es 0: si la zona está oscura, el color resultante también lo está.
  const l = Math.min(maxL, hsl.l);
  const boostedRgb = hslToRgb(hsl.h, s, l);

  let dimming: number;
  // Piso proporcional a maxBrightness — pero adaptativo a la luminosidad de la escena:
  // Escenas oscuras (brightness bajo) → piso bajo (el foco puede bajar mucho)
  // Escenas brillantes (brightness alto) → piso normal (mantiene la dinámica)
  const sceneFactor = Math.pow(brightness / 100, 0.6);
  const floor = Math.round(maxBrightness * (0.05 + 0.20 * sceneFactor));

  if (brightness < 10) {
    // Transición logarítmica suave sin escalón hacia el apagado
    const u = (brightness - 2) / 8;
    const top = Math.max(
      floor,
      Math.min(maxBrightness, Math.round(Math.pow(0.1, gamma) * maxBrightness))
    );
    dimming = Math.round(Math.log10(1 + 9 * u) * top);
  } else {
    const t = brightness / 100;
    dimming = Math.round(Math.pow(t, gamma) * maxBrightness);
    dimming = Math.max(floor, Math.min(maxBrightness, dimming));
  }

  return {
    color: {
      r: Math.max(0, Math.min(255, boostedRgb.r)),
      g: Math.max(0, Math.min(255, boostedRgb.g)),
      b: Math.max(0, Math.min(255, boostedRgb.b)),
    },
    dimming,
  };
}

export interface ZoneRect {
  x1: number;
  y1: number;
  w: number;
  h: number;
}

// Calcula el área de muestreo según la zona y el ancho de borde específico
export function getZoneRect(
  vw: number,
  vh: number,
  zone: ZoneId,
  borderWidth: number
): ZoneRect {
  const bw = Math.min(borderWidth, Math.min(vw / 2, vh / 2));
  let x1 = 0;
  let y1 = 0;
  let x2 = vw;
  let y2 = vh;

  switch (zone) {
    case 'left':
      x1 = 0;
      y1 = bw;
      x2 = Math.min(bw, vw);
      y2 = vh - bw;
      break;
    case 'right':
      x1 = Math.max(0, vw - bw);
      y1 = bw;
      x2 = vw;
      y2 = vh - bw;
      break;
    case 'top':
      x1 = bw;
      y1 = 0;
      x2 = vw - bw;
      y2 = Math.min(bw, vh);
      break;
    case 'bottom':
      x1 = bw;
      y1 = Math.max(0, vh - bw);
      x2 = vw - bw;
      y2 = vh;
      break;
    case 'top-left':
      x1 = 0;
      y1 = 0;
      x2 = Math.min(bw, vw);
      y2 = Math.min(bw, vh);
      break;
    case 'top-right':
      x1 = Math.max(0, vw - bw);
      y1 = 0;
      x2 = vw;
      y2 = Math.min(bw, vh);
      break;
    case 'bottom-left':
      x1 = 0;
      y1 = Math.max(0, vh - bw);
      x2 = Math.min(bw, vw);
      y2 = vh;
      break;
    case 'bottom-right':
      x1 = Math.max(0, vw - bw);
      y1 = Math.max(0, vh - bw);
      x2 = vw;
      y2 = vh;
      break;
    case 'center': {
      const cw = Math.floor(vw / 3);
      const ch = Math.floor(vh / 3);
      x1 = Math.floor((vw - cw) / 2);
      y1 = Math.floor((vh - ch) / 2);
      x2 = x1 + cw;
      y2 = y1 + ch;
      break;
    }
    case 'all':
    default:
      x1 = 0;
      y1 = 0;
      x2 = vw;
      y2 = vh;
      break;
  }

  return {
    x1: Math.max(0, Math.floor(x1)),
    y1: Math.max(0, Math.floor(y1)),
    w: Math.max(1, Math.floor(x2 - x1)),
    h: Math.max(1, Math.floor(y2 - y1)),
  };
}

export interface DynamicMemory {
  t: number;
  r: number;
  g: number;
  b: number;
  warm: boolean;
  prev: Float32Array | null;
  pw: number;
  ph: number;
}

export function createDynamicMemory(): DynamicMemory {
  return { t: 0, r: 0, g: 0, b: 0, warm: false, prev: null, pw: 0, ph: 0 };
}

// Analiza los píxeles de un ImageData y aplica el algoritmo seleccionado
export function analyzePixels(
  data: Uint8ClampedArray,
  cw: number,
  ch: number,
  algorithm: AlgorithmId,
  tune: AlgorithmTune,
  dynMem: DynamicMemory,
  zoneId: ZoneId = 'all'
): { rawColor: RGB; brightness: number; info?: string } {
  const n = cw * ch;
  if (n === 0) return { rawColor: { r: 0, g: 0, b: 0 }, brightness: 0 };

  // Motor Perceptual de Color (OKLab + Saliencia + Cobertura sublineal + Subtítulos + Temporalidad)
  if (algorithm === 'perceptual') {
    return analyzePerceptualZone(data, cw, ch, zoneId, tune);
  }

  const beta = tune.weightedBeta;
  const floorW = tune.weightedFloor;
  const levels = Math.max(2, Math.round(tune.dominantLevels));
  const qmax = levels - 1;
  const isDyn = algorithm === 'dynamic';

  const satHist = isDyn ? new Int32Array(32) : null;
  const briHist = isDyn ? new Int32Array(32) : null;

  let tr = 0;
  let tg = 0;
  let tb = 0;
  let tl = 0;

  let satBest = -1;
  let sR = 0;
  let sG = 0;
  let sB = 0;

  let wr = 0;
  let wg = 0;
  let wb = 0;
  let ws = 0;

  const hist: Record<number, number> = {};
  const rs: number[] = [];
  const gs: number[] = [];
  const bs: number[] = [];
  const needMedian = algorithm === 'median';

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;

    tr += r;
    tg += g;
    tb += b;
    tl += lum;

    const mx = Math.max(r, g, b);
    const mn = Math.min(r, g, b);
    const sat = mx > 0 ? (mx - mn) / mx : 0;

    // Saturated
    if (
      sat > satBest &&
      mx >= tune.satMinBright &&
      sat * 100 >= tune.satMinSat
    ) {
      satBest = sat;
      sR = r;
      sG = g;
      sB = b;
    }

    // Dynamic histograms
    if (isDyn && satHist && briHist) {
      satHist[Math.min(31, (sat * 32) | 0)]++;
      briHist[Math.min(31, mx >> 3)]++;
    }

    // Weighted
    const w = Math.pow(lum / 255, beta) + floorW;
    wr += r * w;
    wg += g * w;
    wb += b * w;
    ws += w;

    // Dominant
    if (algorithm === 'dominant') {
      const key =
        Math.round((r * qmax) / 255) * levels * levels +
        Math.round((g * qmax) / 255) * levels +
        Math.round((b * qmax) / 255);
      hist[key] = (hist[key] || 0) + 1;
    }

    // Median
    if (needMedian) {
      rs.push(r);
      gs.push(g);
      bs.push(b);
    }
  }

  const avgR = (tr / n) | 0;
  const avgG = (tg / n) | 0;
  const avgB = (tb / n) | 0;
  const brightness = Math.round((tl / n / 255) * 100);

  let fr = avgR;
  let fg = avgG;
  let fb = avgB;

  switch (algorithm) {
    case 'average':
      fr = avgR;
      fg = avgG;
      fb = avgB;
      break;

    case 'weighted':
      if (ws > 0) {
        fr = Math.round(wr / ws);
        fg = Math.round(wg / ws);
        fb = Math.round(wb / ws);
      }
      break;

    case 'median':
      rs.sort((a, b) => a - b);
      gs.sort((a, b) => a - b);
      bs.sort((a, b) => a - b);
      const mid = (n / 2) | 0;
      fr = rs[mid] ?? avgR;
      fg = gs[mid] ?? avgG;
      fb = bs[mid] ?? avgB;
      break;

    case 'dominant': {
      let bestKey = -1;
      let bestCount = -1;
      for (const k in hist) {
        if (hist[k] > bestCount) {
          bestCount = hist[k];
          bestKey = Number(k);
        }
      }
      const domShare = (bestCount / n) * 100;
      if (bestKey >= 0 && domShare >= tune.dominantMinShare) {
        const qr = Math.floor(bestKey / (levels * levels));
        const rem = bestKey - qr * levels * levels;
        const qg = Math.floor(rem / levels);
        const qb = rem - qg * levels;
        fr = Math.round(((qr + 0.5) * 255) / levels);
        fg = Math.round(((qg + 0.5) * 255) / levels);
        fb = Math.round(((qb + 0.5) * 255) / levels);
      }
      break;
    }

    case 'saturated':
      if (satBest >= 0) {
        fr = sR;
        fg = sG;
        fb = sB;
      }
      break;

    case 'dynamic': {
      const DYN_B = 8;
      const dynBw = Math.ceil(cw / DYN_B);
      const dynBh = Math.ceil(ch / DYN_B);
      let dynScore = -1;
      let dR = avgR;
      let dG = avgG;
      let dB = avgB;

      if (satHist && briHist) {
        const satC = new Float64Array(32);
        let accS = 0;
        for (let k = 0; k < 32; k++) {
          const prev = accS;
          accS += satHist[k];
          satC[k] = prev + accS / 2;
        }

        const briC = new Float64Array(32);
        let accB = 0;
        for (let k = 0; k < 32; k++) {
          const prev = accB;
          accB += briHist[k];
          briC[k] = prev + accB / 2;
        }

        const prev =
          dynMem.prev && dynMem.pw === dynBw && dynMem.ph === dynBh
            ? dynMem.prev
            : null;
        const next = new Float32Array(dynBw * dynBh * 3);

        for (let by = 0, bi = 0; by < dynBh; by++) {
          const y0 = by * DYN_B;
          const y1 = Math.min(y0 + DYN_B, ch);
          for (let bx = 0; bx < dynBw; bx++, bi++) {
            const x0 = bx * DYN_B;
            const x1 = Math.min(x0 + DYN_B, cw);
            let sr = 0;
            let sg = 0;
            let sb = 0;
            let cnt = 0;

            for (let y = y0; y < y1; y++) {
              let idx = (y * cw + x0) * 4;
              for (let x = x0; x < x1; x++, idx += 4) {
                sr += data[idx];
                sg += data[idx + 1];
                sb += data[idx + 2];
                cnt++;
              }
            }

            if (cnt === 0) continue;
            const br = sr / cnt;
            const bg = sg / cnt;
            const bb = sb / cnt;
            next[bi * 3] = br;
            next[bi * 3 + 1] = bg;
            next[bi * 3 + 2] = bb;

            const mx = Math.max(br, bg, bb);
            const mn = Math.min(br, bg, bb);
            const sat = mx > 0 ? (mx - mn) / mx : 0;
            const rel =
              0.5 * (satC[Math.min(31, (sat * 32) | 0)] / n) +
              0.5 * (briC[Math.min(31, (mx / 8) | 0)] / n);
            const abs = 0.5 * sat + 0.5 * (mx / 255);
            let score = rel * (0.5 + 0.5 * abs);

            if (prev) {
              const diff =
                (Math.abs(br - prev[bi * 3]) +
                  Math.abs(bg - prev[bi * 3 + 1]) +
                  Math.abs(bb - prev[bi * 3 + 2])) /
                765;
              score = Math.min(1, score + 0.35 * diff);
            }

            if (score > dynScore) {
              dynScore = score;
              dR = Math.round(br);
              dG = Math.round(bg);
              dB = Math.round(bb);
            }
          }
        }

        dynMem.prev = next;
        dynMem.pw = dynBw;
        dynMem.ph = dynBh;

        // Mezcla suave con envolvente temporal attack/release
        const accent = { r: dR, g: dG, b: dB };
        const average = { r: avgR, g: avgG, b: avgB };
        const targetMix = Math.min(1, Math.max(0, dynScore));
        const mixed = mixLinear(average, accent, targetMix);
        fr = mixed.r;
        fg = mixed.g;
        fb = mixed.b;
      }
      break;
    }
  }

  return {
    rawColor: {
      r: Math.max(0, Math.min(255, fr)),
      g: Math.max(0, Math.min(255, fg)),
      b: Math.max(0, Math.min(255, fb)),
    },
    brightness,
  };
}

export interface DetailedAnalysisResult {
  rawColor: RGB;
  brightness: number;
  info: string;
}

export function analyzeZoneDetailed(
  data: Uint8ClampedArray,
  cw: number,
  ch: number,
  algorithm: AlgorithmId,
  tune: AlgorithmTune,
  dynMem: DynamicMemory
): DetailedAnalysisResult {
  const basic = analyzePixels(data, cw, ch, algorithm, tune, dynMem);
  let info = '';

  const hex = rgbToHex(basic.rawColor.r, basic.rawColor.g, basic.rawColor.b);

  switch (algorithm) {
    case 'weighted':
      info = `β ${tune.weightedBeta.toFixed(2)} · Suelo ${tune.weightedFloor.toFixed(2)} · Lum ${basic.brightness}% · ${hex}`;
      break;
    case 'average':
      info = `Promedio aritmético estable de la zona · Lum ${basic.brightness}% · ${hex}`;
      break;
    case 'dominant':
      info = `Agrupación en ${tune.dominantLevels} niveles · Mín share ${tune.dominantMinShare}% · ${hex}`;
      break;
    case 'median':
      info = `Mediana estadística sin valores atípicos · Lum ${basic.brightness}% · ${hex}`;
      break;
    case 'saturated':
      info = `Tono más vívido (Filtro mín brillo ${tune.satMinBright}, sat ${tune.satMinSat}%) · ${hex}`;
      break;
    case 'dynamic':
      info = `Bloques 8×8 adaptativos con seguimiento de movimiento · ${hex}`;
      break;
  }

  return {
    rawColor: basic.rawColor,
    brightness: basic.brightness,
    info,
  };
}
